// useAppViewScope
// ----------------
// Shared hook for the My Apps / All Apps toggle that appears on both
// the Dashboard and the Applications page. Persists the user's choice
// in localStorage so it survives reloads, and keys the storage entry
// by user email so a shared browser doesn't cross-contaminate
// preferences between accounts.
//
// Default is false (All Apps) when there's no stored preference.
// Storage failures (private browsing, quota, disabled storage) are
// swallowed — the in-memory state still updates for the current
// session, the preference just won't survive a reload.
//
// Use:
//   const [displayMyApps, setDisplayMyApps] = useAppViewScope(user.emailaddress);

import { useState, useCallback, useEffect } from "react";

const STORAGE_KEY_PREFIX = "nimis.appViewScope.";

function readStored(storageKey: string | null): boolean {
  if (!storageKey) return false;
  try {
    return localStorage.getItem(storageKey) === "true";
  } catch {
    return false;
  }
}

export function useAppViewScope(
  userEmail: string | null | undefined,
): [boolean, (value: boolean) => void] {
  const storageKey = userEmail ? STORAGE_KEY_PREFIX + userEmail : null;

  const [displayMyApps, setDisplayMyAppsState] = useState<boolean>(() =>
    readStored(storageKey),
  );

  // Sync state if the user identity changes mid-session (login →
  // logout → different user, or async user-data load). The useState
  // initializer only runs once, so without this hook a second user on
  // the same browser would see the first user's preference until they
  // toggle.
  useEffect(() => {
    setDisplayMyAppsState(readStored(storageKey));
  }, [storageKey]);

  const setDisplayMyApps = useCallback(
    (value: boolean) => {
      setDisplayMyAppsState(value);
      if (!storageKey) return;
      try {
        localStorage.setItem(storageKey, value ? "true" : "false");
      } catch {
        // Storage unavailable — session-only state, no persistence.
      }
    },
    [storageKey],
  );

  return [displayMyApps, setDisplayMyApps];
}
